import { ComponentType, FC, useEffect, useRef } from 'react'
import { Box, BoxProps, Button, Grid, GridProps, styled, Typography } from '@mui/material'
import { IFormInputBase } from './types'
import { getErrorMessage } from './helpers'
import { getFormFields, getFormPreSubmit, getFormValidators, isFormRegistered } from './decorator'
import FormValidator, { IPartialError } from './validator'
import createFormBase, { IFormBaseSlots } from './create.form-base'
import createInput from './create.input'

const FormBase = createFormBase<any>({})
const InputInstance = createInput<any>({})

export interface IDecoratorFieldProps<T = any> extends IFormInputBase<T> {
  type?: any
  visible?: boolean | ((data: T) => boolean)
  renderInput?: ComponentType<IFormInputBase<T>>
}

export const DecoratorField: FC<IDecoratorFieldProps> = ({ type: inputType, renderInput, ...props }) => {
  const name = props.name?.toString() || ''
  const defaultValue = props.data?.[name] || props.defaultValue

  return FormBase.mapContext((context) => {
    const { messageErrors, onBlur } = context
    const err = getErrorMessage(context.messageErrors, props.name as any)
    if (renderInput) {
      const InputComp = renderInput
      return <InputComp {...props} messageErrors={messageErrors} onBlur={onBlur} formContext={context} />
    }
    switch (inputType) {
      case String:
        return <InputInstance {...props} messageErrors={messageErrors} onBlur={onBlur} formContext={context} />
      case Number:
        return (
          <InputInstance
            {...props}
            slots={{ textFieldProps: { type: 'number' }, ...props.slots }}
            messageErrors={messageErrors}
            onBlur={onBlur}
            formContext={context}
          />
        )
      case Boolean:
        return (
          <div>
            <label>{props.label || name}: </label>
            <input name={name} type='checkbox' defaultChecked={defaultValue} />
            <span style={{ color: 'red' }}>{err.error ? err.message : ''}</span>
          </div>
        )
      default:
        return <div>Unsupported field type for {name}</div>
    }
  })
}

export interface IDecoratorFormSlots {
  top?: React.ReactNode
  bottom?: React.ReactNode
  isFooter?: React.ReactNode | boolean
  contentProps?: GridProps
  formBase?: IFormBaseSlots
  footer?: ComponentType<any>
  footerProps?: BoxProps & { [key: string]: any }
  footerSlots?: { after?: React.ReactNode; before?: React.ReactNode }
}

export interface IDecoratorFormProps<T = any> {
  configClass?: any
  data?: Partial<T>
  onSubmit?: (data: Partial<T>, signal?: AbortSignal) => Promise<void>
  onError?: (error: any) => void
  onSubmitError?: (error: IPartialError<T>) => void
  onSuccess?: (data: Partial<T>) => void
  enableStickyFooter?: boolean
  slots?: IDecoratorFormSlots
}

export const DecoratorForm: FC<IDecoratorFormProps> = (props) => {
  const { slots } = props
  const fields = getFormFields(props.configClass)
  const validators = getFormValidators(props.configClass)
  const preSubmitConfig = getFormPreSubmit(props.configClass)
  const abortControllerRef = useRef<AbortController | null>(null)

  // If the provided configClass isn't registered via @registerForm,
  // show a simple, friendly error UI instead of throwing.
  if (!isFormRegistered(props.configClass)) {
    const name = props.configClass?.name || 'Unknown'
    return (
      <Box sx={{ border: '1px dashed #ccc', padding: 12, borderRadius: 4, color: '#333', background: '#fafafa' }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, marginBottom: 6 }}>
          Form configuration not registered
        </Typography>
        <Typography variant='body1'>
          Configuration for "{name}" was not found. Did you forget to add <code>@registerForm</code> to the class?
        </Typography>
      </Box>
    )
  }

  const handleSubmit = async (data: any) => {
    // Create new AbortController for this submit
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    const signal = abortController.signal

    let processedData = data
    try {
      // Execute presubmit handler if exists
      if (preSubmitConfig?.handler) {
        processedData = await preSubmitConfig.handler(data, props.data)
        // If handler returns null/undefined, use original data
        if (processedData === null || processedData === undefined) {
          processedData = data
        }
      }

      // Call the original onSubmit with processed data and abort signal
      if (props.onSubmit) {
        await props.onSubmit(processedData, signal)
        if (props.onSuccess) {
          props.onSuccess(processedData)
        }
      }
    } catch (error) {
      if (props.onError) {
        props.onError(error)
      }
      throw error
    }
  }

  const renderFooter = () => {
    if (slots?.isFooter === false) return null
    if (slots?.footer) {
      const FooterComponent = slots.footer
      return <FooterComponent />
    }
    const footerClassName = [slots?.footerProps?.className]
    if (props.enableStickyFooter) footerClassName.push('sticky-footer')
    return (
      <FooterStyled {...slots?.footerProps} className={footerClassName.filter(Boolean).join(' ')}>
        {slots?.footerSlots?.before}
        <Button variant='contained' color='primary' type='submit' size='small' sx={{ flex: '0 0 auto' }}>
          Submit
        </Button>
        {slots?.footerSlots?.after}
      </FooterStyled>
    )
  }

  useEffect(() => {
    return () => {
      // Abort any pending request when component unmounts
      abortControllerRef.current?.abort()
    }
  }, [])

  return (
    <FormBase.Form onSubmit={handleSubmit} validate={new FormValidator(validators)} onError={props.onSubmitError} slots={slots?.formBase}>
      {props.slots?.top}
      <Grid container spacing={2} {...slots?.contentProps}>
        {fields.map((field) => {
          const { disabled, visible, size, ...fieldOther } = field
          // Check visibility, default is visible (true or undefined)
          const isVisible = typeof visible === 'function' ? visible(props.data) : visible !== false
          if (!isVisible) return null
          // Check disabled state, default is enabled (false or undefined)
          const isDisabled = typeof disabled === 'function' ? disabled(props.data) : disabled === true
          return (
            <Grid item key={field.name.toString()} xs={12} {...size}>
              <DecoratorField data={props.data} {...fieldOther} disabled={isDisabled} />
            </Grid>
          )
        })}
      </Grid>
      {renderFooter()}
      {props.slots?.bottom}
    </FormBase.Form>
  )
}
export default DecoratorForm

type IDecoratorFormOptions<T> = Partial<Omit<IDecoratorFormProps<T>, 'configClass'>>

export const createDecoratorForm = function <T>(configClass: any, params?: IDecoratorFormOptions<T>): ComponentType<IDecoratorFormProps<T>> {
  const DecoratorFormComp: FC<IDecoratorFormProps<T>> = (props) => {
    return <DecoratorForm configClass={configClass} {...params} {...props} />
  }
  return DecoratorFormComp
}

const FooterStyled = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: theme.spacing(1),
  '&.sticky-footer': {
    position: 'sticky',
    bottom: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: theme.zIndex.appBar,
    boxShadow: 'rgba(145, 158, 171, 0.1) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 0px 24px -4px'
  }
}))
